import './headerPair.scss';

import cn from 'classnames';
import { motion } from 'framer-motion';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { generatePath, NavLink } from 'react-router-dom';

import { useIsDark } from '@/shared/theme';

import { ApiCexPairs, ApiProjects } from 'api';
import { buildSymbolStatusKey, useCexSymbolStatuses } from 'entities/cex';
import { UDPairModal } from 'modals';
import ProjectListIconPairs from 'shared/components/PlatformIcon';
import { ROUTE_PATHS } from 'shared/consts/routes';
import { useTypedDispatch, useTypedSelector } from 'store';
import {
  setPairError,
  setPairLoading,
  setSelectedCexPair,
  setSelectedDexPair,
  setSelectedSolPair,
  updateCexPairPrice,
} from 'store/slices/pairs';
import { IProject } from 'types/project';
import { Tooltip } from 'ui/Tooltip';
import { getPairLinkedBots } from 'utils/getPairLinkedBots';
import { cexConfigs, EExchange } from 'web3';

import { PairHeaderSkeleton } from './PairHeaderSkeleton';
import { PairViewToggle } from './PairViewToggle';

import { CogIcon } from 'assets/icons';

interface ICexPairHeaderProps {
  pairId: number;
  projectId: number;
}

const capitalizeWord = (value: string) => (value ? value[0].toUpperCase() + value.slice(1) : '');

const formatExchangeName = (value: string) => {
  const [rawName, chain] = value.split(':');

  const name = rawName
    .split('_')
    .map(part => (/^v\d+$/i.test(part) ? part.toUpperCase() : capitalizeWord(part)))
    .join(' ');

  return chain ? `${name} (${chain.toUpperCase()})` : name;
};

const CexPairHeader: React.FC<ICexPairHeaderProps> = ({ pairId, projectId }) => {
  const dispatch = useTypedDispatch();
  const { pathname } = useLocation();
  const cexPair = useTypedSelector(store => store.pairs.selectedCexPair);
  const projects = useTypedSelector(store => store.projects.projects);
  const isAdmin = useTypedSelector(store => store.auth.isAdmin);
  const [project, setProject] = useState<IProject | null>(null);
  const [updateModalOpened, setUpdateModalOpened] = useState<boolean>(false);
  const isDark = useIsDark();

  const onOpenUpdateModalOpened = useCallback(() => {
    setUpdateModalOpened(true);
  }, []);

  const onCloseUpdateModalOpened = useCallback(() => {
    setUpdateModalOpened(false);
  }, []);

  useEffect(() => {
    const setupSelectProjectAndPair = async () => {
      if (projects) {
        try {
          dispatch(setSelectedCexPair(null));
          dispatch(setSelectedDexPair(null));
          dispatch(setSelectedSolPair(null));
          dispatch(setPairLoading(true));
          dispatch(setPairError(false));

          const [activeProject, activePair] = await Promise.all([
            ApiProjects.getProjectById(projectId),
            ApiCexPairs.getCexPairById(pairId),
          ]);

          if (activeProject.isSuccess && activePair.isSuccess) {
            setProject({
              created_at: activeProject.data.created_at,
              id: activeProject.data.id,
              name: activeProject.data.name,
              cexPairs: [],
              dexPairs: [],
              solPairs: [],
              updated_at: '',
              notes: activeProject.data.notes,
            });

            if ('cex' in activePair.data) {
              dispatch(setSelectedCexPair(activePair.data));

              setTimeout(() => {
                dispatch(updateCexPairPrice({ pair: activePair.data }));
              }, 0);
            }

            await getPairLinkedBots({
              pairId: activePair.data.id,
              dispatch,
              exchange: EExchange.cex,
            });
          } else {
            dispatch(setPairError(true));
          }
        } catch (error) {
          console.log(error);
        } finally {
          dispatch(setPairLoading(false));
        }
      }
    };

    setupSelectProjectAndPair();
  }, [dispatch, pathname, projects, projectId, pairId]);

  const symbolStatusItems = useMemo(
    () =>
      cexPair
        ? [
            {
              cex: cexPair.cex,
              base: cexPair.token_base.symbol,
              quote: cexPair.token_quote.symbol,
            },
          ]
        : [],
    [cexPair],
  );

  const { data: symbolStatuses } = useCexSymbolStatuses({
    items: symbolStatusItems,
    enabled: symbolStatusItems.length > 0,
  });

  const statusKey =
    cexPair &&
    buildSymbolStatusKey(cexPair.cex, cexPair.token_quote.symbol, cexPair.token_base.symbol);
  const isSt = statusKey ? symbolStatuses?.[statusKey]?.isSt : false;

  if (!project || !cexPair) {
    return <PairHeaderSkeleton />;
  }

  return (
    <div className="mm-header__header-pair">
      {project && cexPair && (
        <>
          <div className="mm-header__header-pair__info">
            <div className="pair-name">
              <span>{cexPair.token_base.symbol}</span>
              <span>/</span>
              <span>{cexPair.token_quote.symbol}</span>
              {isSt && <span> (ST)</span>}
            </div>
            {isAdmin && (
              <motion.div
                whileHover={{ rotate: 180, scale: 1.3 }}
                transition={{ duration: 0.3 }}
                className="pair-settings"
                onClick={onOpenUpdateModalOpened}
              >
                <CogIcon />
              </motion.div>
            )}
            <NavLink
              to={generatePath(ROUTE_PATHS.project, { id: project.id.toString() })}
              className="project-name"
            >
              {project.name}
            </NavLink>
            <Tooltip
              trigger={
                <div
                  className={cn('header-trading-pair-logo-header', cexPair.cex, {
                    'bg-white': isDark,
                    'bg-bg-primary': !isDark,
                  })}
                >
                  <ProjectListIconPairs platform={cexPair.cex} className="exchange-icon" />
                </div>
              }
              content={formatExchangeName(cexPair.cex)}
              options={{ placement: 'top', offset: 8 }}
            />

            {isAdmin && (
              <div className="ml-3 flex items-center gap-3">
                <PairViewToggle projectId={projectId} pairId={pairId} venue="cex" />
              </div>
            )}
          </div>
          <div className="mm-header__header-pair__bottom">
            <a
              className={cn('scan-link rounded-full p-[2px]')}
              target="_blank"
              rel="noreferref noreferrer"
              href={cexConfigs[cexPair.cex].cexLink(
                cexPair.token_base.symbol,
                cexPair.token_quote.symbol,
              )}
            >
              <ProjectListIconPairs
                platform={cexPair.cex}
                className="header-trading-pair-logo-bottom"
                isIcon
              />
            </a>
            {cexPair.notes && isAdmin && <span>{cexPair.notes}</span>}
          </div>
          {updateModalOpened && (
            <UDPairModal
              exchange={EExchange.cex}
              onClose={onCloseUpdateModalOpened}
              onOpen={onOpenUpdateModalOpened}
              pairId={cexPair.id}
              projectId={project.id}
              pairSymbol={cexPair.symbol}
              initialNotes={cexPair.notes ?? ''}
            />
          )}
        </>
      )}
    </div>
  );
};

export default CexPairHeader;
